import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useSpring, useMotionTemplate } from 'framer-motion';
import { SparklesIcon, CheckIcon } from 'lucide-react';
import { usePressBloom } from './PressBloom';
interface AskAIButtonProps {
  selected: boolean;
  onClick: () => void;
  className?: string;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * The Ask AI action — quietly alive rather than attention-seeking.
 *
 * At rest a low-contrast indigo/purple hue drifts slowly through the fill.
 * Hover lets light follow the cursor. Pressing gives a clear, tactile
 * squeeze, then light blooms from the point you touched, a ring of light
 * leaves the outline, and the sparkle turns once into the check.
 */
export function AskAIButton({
  selected,
  onClick,
  className = ''
}: AskAIButtonProps) {
  const reduce = useReducedMotion();
  // Held-down state, tracked directly so the fill reliably changes while
  // the pointer is pressed — and where it was pressed, so the charge
  // gathers from under the fingertip.
  const [held, setHeld] = useState(false);
  const [holdAt, setHoldAt] = useState({
    x: 50,
    y: 50
  });
  const {
    trigger,
    fillLayer,
    ringLayer
  } = usePressBloom('rounded-lg');
  const mx = useMotionValue(50);
  const my = useMotionValue(50);
  const sx = useSpring(mx, {
    stiffness: 260,
    damping: 28,
    mass: 0.5
  });
  const sy = useSpring(my, {
    stiffness: 260,
    damping: 28,
    mass: 0.5
  });
  const spotlight = useMotionTemplate`radial-gradient(90px circle at ${sx}% ${sy}%, rgba(168,85,247,1) 0%, rgba(168,85,247,0.55) 40%, rgba(168,85,247,0) 75%), radial-gradient(28px circle at ${sx}% ${sy}%, rgba(255,255,255,0.22), rgba(255,255,255,0) 100%)`;
  const place = (e: React.PointerEvent<HTMLButtonElement>, jump: boolean) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width * 100;
    const y = (e.clientY - r.top) / r.height * 100;
    if (jump) {
      mx.jump(x);
      my.jump(y);
      sx.jump(x);
      sy.jump(y);
    } else {
      mx.set(x);
      my.set(y);
    }
  };
  return <motion.button onPointerEnter={e => place(e, true)} onPointerMove={e => place(e, false)} onPointerDown={e => {
    const r = e.currentTarget.getBoundingClientRect();
    setHoldAt({
      x: (e.clientX - r.left) / r.width * 100,
      y: (e.clientY - r.top) / r.height * 100
    });
    setHeld(true);
  }} onPointerUp={() => setHeld(false)} onPointerLeave={() => setHeld(false)} onPointerCancel={() => setHeld(false)} onKeyDown={e => {
    if (e.key === ' ' || e.key === 'Enter') {
      setHoldAt({
        x: 50,
        y: 50
      });
      setHeld(true);
    }
  }} onKeyUp={() => setHeld(false)} onBlur={() => setHeld(false)} onClick={e => {
    e.stopPropagation();
    trigger(e);
    onClick();
  }} whileHover={reduce ? undefined : 'hover'} whileTap={reduce ? undefined : 'press'} variants={{
    press: {
      scale: 0.94
    }
  }} transition={{
    type: 'spring',
    stiffness: 600,
    damping: 30
  }} aria-pressed={selected} className={`group/ask relative isolate flex shadow-sm transition-shadow duration-300 ease-out ${selected ? '' : 'hover:shadow-[0_6px_16px_-6px_rgba(124,58,237,0.55)]'} items-center justify-center gap-1.5 px-3 py-2 text-[12.5px] font-bold text-white rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-indigo/40 focus-visible:ring-offset-2 ${className}`} data-id="element-62" data-mp="2b2wz85c7jv4s">
      <span aria-hidden className="absolute inset-0 rounded-lg overflow-hidden bg-primary-indigo" data-id="element-63" data-mp="1fuv232x8guw7">
        <span className={`ask-ai-drift absolute inset-0 bg-gradient-to-r from-primary-indigo via-ai-purple to-primary-indigo bg-[length:300%_100%] transition-opacity duration-300 ease-out ${selected ? 'opacity-0' : 'opacity-100'}`} data-id="element-64" data-mp="1b1ig70jxv275" />
        {!selected && <motion.span className="absolute inset-0 opacity-0 group-hover/ask:opacity-100 transition-opacity duration-300 ease-out" style={{
        backgroundImage: spotlight
      }} data-id="element-65" data-mp="1ys5pnq9i5zvt" />}
        <span className={`absolute inset-0 bg-dark-navy transition-opacity duration-300 ease-out ${selected ? 'opacity-100' : 'opacity-0'}`} data-id="element-66" data-mp="1ne2lyt2x2j3j" />
        {/* Held down: the button "charges". A vivid violet core gathers
            under the fingertip and spreads through the fill, the base
            deepens toward navy around it, and an inner edge of light
            appears — a clearly different colour state while you hold. */}
        <span className="absolute inset-0 transition-opacity ease-out" style={{
        opacity: held ? 1 : 0,
        transitionDuration: held ? '120ms' : '260ms',
        background: `radial-gradient(140% 220% at ${holdAt.x}% ${holdAt.y}%, #A855F7 0%, #7C3AED 35%, #4338CA 70%, #1E1B4B 100%)`
      }} data-id="element-67" data-mp="1sbuik0eiate2" />
        <span className="absolute inset-0 rounded-lg transition-opacity ease-out" style={{
        opacity: held ? 1 : 0,
        transitionDuration: held ? '120ms' : '260ms',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.28), inset 0 0 14px rgba(216,180,254,0.55)'
      }} data-id="element-68" data-mp="2psb19kgqpu3t" />
        {fillLayer}
        <span className="absolute inset-x-0 top-0 h-px bg-white/25" data-id="element-69" data-mp="33w8ml79nc8cs" />
      </span>

      {ringLayer}

      <span className="relative z-10 w-3.5 h-3.5 flex-shrink-0" data-id="element-70" data-mp="2g7z5gindo8rz">
        <AnimatePresence mode="wait" initial={false} data-id="element-71" data-mp="2c5cboc75cds5">
          {selected ? <motion.span key="check" className="absolute inset-0 flex" initial={{
          opacity: 0,
          scale: 0.5,
          rotate: -90
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} exit={{
          opacity: 0,
          scale: 0.6
        }} transition={{
          type: 'spring',
          stiffness: 500,
          damping: 26
        }} data-id="element-72" data-mp="2c7f6d9j8uv37">
              <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} data-id="element-73" data-mp="f06arukhgk6" />
            </motion.span> : <motion.span key="sparkle" className="absolute inset-0 flex" initial={{
          opacity: 0,
          scale: 0.6,
          rotate: 90
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} exit={{
          opacity: 0,
          scale: 0.4,
          rotate: 180
        }} variants={{
          hover: {
            rotate: 12,
            scale: 1.06
          }
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} data-id="element-74" data-mp="8fy2ys73jbqq">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-75" data-mp="3kvtc6qp6q6ax" />
            </motion.span>}
        </AnimatePresence>
      </span>

      <span className="relative z-10 truncate" data-id="element-76" data-mp="3uwc488qwkt3x">
        <AnimatePresence mode="wait" initial={false} data-id="element-77" data-mp="3uuhhqpzzgsxq">
          <motion.span key={selected ? 'sel' : 'ask'} className="inline-block" initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.16,
          ease: EASE
        }} data-id="element-78" data-mp="3h4r147gjsso">
            {selected ? 'Selected' : 'Ask AI'}
          </motion.span>
        </AnimatePresence>
      </span>
    </motion.button>;
}